<template>
  <a-modal
    :open="visible"
    wrap-class-name="xmov-normal-modal"
    centered
    width="952px"
    :footer="null"
    :title="$t('yan-bi-xiang-qing')"
    @cancel="visible = false"
  >
    <div class="credit-table">
      <div class="flex justify-between items-center">
        <XCardTab
          v-model:model-value="check_all"
          name="credit_coin_consume_history"
          @change="() => fetchData()"
        />
        <a-checkbox
          class="transform -translate-y-5px"
          :checked="onlyValid"
          :disabled="loading"
          @change="onCheckValid"
        >
          <span class="text-font-1">
            {{ $t('jin-kan-you-xiao') }}
          </span>
        </a-checkbox>
      </div>
      <div class="mb-[6px] h-[1px] bg-back-4 -translate-y-[1px]"></div>
      <a-table
        :custom-row="onCustomRender"
        :columns="columns"
        :data-source="dataSource"
        :loading="loading"
        :pagination="false"
        :scroll="{ x: '100%', y: 510 }"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record, text }">
          <template v-if="record.isDefault">
            <div class="h-[36px]"></div>
          </template>
          <template v-else>
            <template v-if="column.dataIndex === 'rights_name'">
              {{ showRightsName(record as ICreditCoinOrder) }}
            </template>
            <template v-if="column.dataIndex === 'used_credit_coin'">
              {{ record.total_credit_coin - record.remained_credit_coin }}
            </template>
            <template v-if="column.dataIndex === 'start_timestamp'">
              {{ text ? timezoneFormat(text, 'YYYY-MM-DD HH:mm:ss') : '' }}
            </template>
            <template v-if="column.dataIndex === 'expired_timestamp'">
              <!-- FIXME: v-if/v-else 会 error -->
              <div
                v-show="isCreditCoinOrder(record.order_type)"
                class="text-[14px] leading-[18px]"
                :style="expiredStyle(text)"
              >
                <div
                  :class="{
                    'text-font-80': !expiredStyle(text)
                      ? checkTime(record)
                      : false,
                  }"
                >
                  {{ checkTime(record) || packageExpireDate }}
                </div>
                <div
                  v-if="!checkTime(record)"
                  class="flex items-center opacity-60"
                >
                  <span>({{ $t('chang-qi-bao-liu') }})&nbsp;</span>
                  <a-popover
                    :title="
                      $t(
                        'yong-jiu-you-xiao-qi-jin-tao-can-you-xiao-qi-nei-ke-yong'
                      )
                    "
                  >
                    <InfoCircleOutlined />
                  </a-popover>
                </div>
              </div>
              <div
                v-show="!isCreditCoinOrder(record.order_type)"
                class="text-[14px] leading-[36px]"
                :style="expiredStyle(text)"
              >
                {{
                  text
                    ? timezoneFormat(text, 'YYYY-MM-DD HH:mm:ss')
                    : $t('yong-jiu-you-xiao')
                }}
              </div>
            </template>
          </template>
        </template>
        <template #emptyText>
          <EmptyView />
        </template>
      </a-table>
    </div>
  </a-modal>
</template>

<script setup lang="ts" name="CreditCoinList">
/**
 * TODO 清空 sorter
 */
import type { ColumnsType } from 'ant-design-vue/es/table/interface'
import type { CheckboxChangeEvent } from 'ant-design-vue/es/checkbox/interface'
import { reqCreditCoinOrder, type ICreditCoinOrder } from '~/api'
import { ORDER_TYPE } from '~/api/interface'
import { useTime } from '~/hooks/useTime'

const { timezoneFormat } = useTime()

const { $t } = useNuxtApp()

const props = defineProps<{ onlyValid: boolean }>()
const emits = defineEmits(['chartData', 'update:onlyValid'])

const visible = ref(false)

let results: ICreditCoinOrder[] = []
function genDefaultData() {
  return new Array(5).fill({ isDefault: true })
}

const columns: ColumnsType<ICreditCoinOrder> = [
  {
    title: $t('yan-bi-lei-xing'),
    width: 150,
    dataIndex: 'rights_name',
  },
  {
    title: $t('zong-yan-bi'),
    width: 100,
    dataIndex: 'total_credit_coin',
  },
  {
    title: $t('xiao-hao-yan-bi'),
    width: 108,
    dataIndex: 'used_credit_coin',
  },
  {
    title: $t('sheng-yu-yan-bi'),
    width: 108,
    dataIndex: 'remained_credit_coin',
  },
  {
    title: $t('sheng-xiao-shi-jian'),
    width: 180,
    dataIndex: 'start_timestamp',
    sorter: (a, b) => a.start_timestamp - b.start_timestamp,
  },
  {
    title: $t('shi-xiao-shi-jian'),
    width: 180,
    dataIndex: 'expired_timestamp',
    sorter: true,
    defaultSortOrder: 'ascend',
  },
]

const userInfo = useUserInfo()

const check_all = ref('0') // 0 套餐，1 全部
const loading = ref(false)
const dataSource = ref<ICreditCoinOrder[]>(genDefaultData())

const { $dayjs, timeFormat } = useTime()

const packageExpireDate = computed(() =>
  timeFormat(userInfo.value?.current_package_expired_time)
)

// 言币购买套餐到期时间需小于权益到期时间才返回
const checkTime = (record: any) => {
  if (!record.expired_time) {
    return ''
  }

  const currentTime = userInfo.value?.current_package_expired_time
  const diff = $dayjs(record.expired_time).diff(currentTime, 'hour')
  return diff < 0 ? timeFormat(record.expired_time) : ''
}

function showRightsName(record: ICreditCoinOrder) {
  // if (record.order_type === ORDER_TYPE.PACKAGE) {
  //   const timeLabel =
  //     record.time_identity === RIGHTS_TIME().month
  //       ? $t('yue-tao-can')
  //       : $t('nian-tao-can')
  //   return record.rights_name + '-' + timeLabel
  // }
  return record.rights_name
}

function isCreditCoinOrder(orderType: ORDER_TYPE) {
  return orderType === ORDER_TYPE.CREDITS
}
function expiredStyle(text: number) {
  return isBefore7DaysFrom(text) ? 'color:#FF913D;' : ''
}
function isBefore7DaysFrom(target: number) {
  if (target === 0) {
    return false
  }

  const now = $dayjs()
  const destString = timezoneFormat(target)
  if (now.isAfter(destString)) {
    return false
  }

  return $dayjs(now.add(7, 'day').format('YYYY-MM-DD HH:ss:mm')).isAfter(
    destString
  )
}
function onCustomRender(record: ICreditCoinOrder) {
  if (record.expired_timestamp <= 0) {
    return {
      style: {
        opacity: 1,
      },
    }
  }
  return {
    style: {
      opacity: $dayjs().isBefore(timezoneFormat(record.expired_timestamp))
        ? 1
        : 0.5,
    },
  }
}

// 过滤“过期”与否
function onFilterValid(onlyValid: boolean) {
  if (!onlyValid) {
    return results
  }

  return results.filter((_) => {
    if (_.expired_timestamp === 0) {
      return true
    }

    return _.status === 1
  })
}

// “仅看有效”
function onCheckValid(e: CheckboxChangeEvent) {
  emits('update:onlyValid', e.target.checked)
  setDataSource(onFilterValid(e.target.checked))
}

// 统计单个类型
function calcNumbers(list: ICreditCoinOrder[]) {
  const stat = {
    remain: 0,
    total: 0,
  }
  list.forEach((order) => {
    stat.remain += order.remained_credit_coin
    stat.total += order.total_credit_coin
  })
  return stat
}

// 统计所有类型数据
function statNumbers() {
  const statAry = {}
  Object.assign(statAry, {
    [ORDER_TYPE.PACKAGE]: calcNumbers(
      dataSource.value.filter(
        (record) => record.order_type === ORDER_TYPE.PACKAGE
      )
    ),
  })
  Object.assign(statAry, {
    [ORDER_TYPE.ACTIVITY]: calcNumbers(
      dataSource.value.filter(
        (record) => record.order_type === ORDER_TYPE.ACTIVITY
      )
    ),
  })
  Object.assign(statAry, {
    [ORDER_TYPE.CREDITS]: calcNumbers(
      dataSource.value.filter(
        (record) => record.order_type === ORDER_TYPE.CREDITS
      )
    ),
  })
  emits('chartData', statAry)
}

async function fetchData(ordering_expired_time = 0) {
  loading.value = true
  const { code, data } = await reqCreditCoinOrder({
    check_all: check_all.value,
    ordering_expired_time,
  })
  loading.value = false
  if (!code && data) {
    results = data.order_list.filter((d) => d.is_show)
    const filtered = onFilterValid(props.onlyValid)
    setDataSource(filtered)
  }
}

const handleTableChange = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  pag: { pageSize: number; current: number },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  filters: any,
  sorter: any
) => {
  if (sorter.field === 'expired_timestamp') {
    fetchData(sorter.order === 'descend' ? 1 : 0)
  }
}

/**
 * 默认 4 条空数据，撑开表格
 * 真实数据个数少于 4，则对空列表进行占位操作
 */
function setDataSource(rawList: ICreditCoinOrder[]) {
  if (rawList.length > dataSource.value.length) {
    dataSource.value = rawList
  } else {
    const defaultData = genDefaultData()
    defaultData.splice(0, rawList.length, ...rawList)
    dataSource.value = defaultData
  }
}

const toggleShow = () => {
  visible.value = !visible.value
}

watch(dataSource, statNumbers)

defineExpose({ reload: fetchData, toggleShow })

// onMounted(fetchData)
watch(
  () => visible.value,
  (cur, bef) => {
    if (cur !== bef && cur) {
      fetchData()
    }
  }
)
</script>

<style lang="scss">
// 定制表格视觉
.credit-table {
  .ant-tabs-nav {
    margin: 0;
  }
  // th.ant-table-column-has-sorters {
  //   background: #fafafa !important;
  // }
  // td.ant-table-column-sort {
  //   background: initial;
  // }
  .ant-table-header {
    th {
      height: 52px;
      // background: #f6f8f9 !important;
    }
  }
  .ant-table-tbody {
    .ant-table-row td {
      padding: 8px 24px !important;
      height: 36px;
      font-size: 14px;
    }
  }
}
</style>
